import { GetFilesProps } from '@/hooks/useFiles';
import { useAuth } from '@/state/auth';
import { GraphQuery, MyResourceKey, UniqueQueryKeys } from '@/types';

export const getQueryKey = {
  getAgents: (variant?: 'basic' | 'enhanced') =>
    [UniqueQueryKeys.AGENTS, ...(variant ? [variant] : [])] as const,
  getEnrich: (type: string, id: string) => [UniqueQueryKeys.ENRICH, type, id],
  getMy: (
    key: MyResourceKey,
    compositeKey?: string,
    to?: string,
    desc?: boolean,
    global?: boolean,
    allTenants?: boolean,
    osint?: boolean,
    praetorian?: boolean
  ) => {
    return [
      UniqueQueryKeys.MY,
      key,
      ...(compositeKey ? [compositeKey] : []),
      ...(to ? [to] : []),
      ...(desc ? [desc] : []),
      ...(global ? [global] : []),
      ...(allTenants ? [allTenants] : []),
      ...(osint ? [osint] : []),
      ...(praetorian ? [praetorian] : []),
    ];
  },
  // ENG-4404: descendant-tree discovery keys. Deliberately DISJOINT from
  // getMy keys — useMy entries are infinite-query shaped ({pages}), these are
  // plain useQueries entries; sharing a key shape would collide two data
  // shapes in one cache slot. The [MY, 'conversation-descendants', anchorId]
  // prefix is the single WS-invalidation target for the whole family.
  getDescendantConversations: (
    anchorId: string,
    unit:
      | { kind: 'parent'; nodeId: string; partition: 'user' | 'tenant' }
      | { kind: 'hunt'; huntId: string }
  ) => [
    UniqueQueryKeys.MY,
    'conversation-descendants',
    anchorId,
    ...(unit.kind === 'parent'
      ? ['parent', unit.nodeId, unit.partition]
      : ['hunt', unit.huntId]),
  ],
  // ENG-4404: upward root-resolution keys. Same disjoint-from-getMy discipline
  // as getDescendantConversations (plain useQueries entries, not {pages}).
  getConversationAncestor: (id: string, partition: 'user' | 'tenant') => [
    UniqueQueryKeys.MY,
    'conversation-ancestor',
    id,
    partition,
  ],
  getAccountAlerts: (account: string) => [
    UniqueQueryKeys.ACCOUNT_ALERTS,
    account,
  ],
  getBackend: () => [UniqueQueryKeys.Backends],
  getFile: (props: GetFilesProps) => [
    UniqueQueryKeys.GET_FILE,
    props.name,
    props.global,
  ],
  // ENG-5214: the cache key must include every request parameter that changes
  // which rows the backend returns, or two semantically-distinct searches
  // collide into one cache slot. `label`, `exact`, and `tree` all steer the
  // `/my` result set (see useGenericSearch's GET/POST branches) yet were
  // previously omitted, so e.g. `{query:'x', label:Seed}` and
  // `{query:'x', label:Risk}` shared a bucket. Placement is deliberate:
  //   - `label` goes FIRST (right after the tag), mirroring getMy's
  //     `[MY, resource, …]` resource-discriminator shape. A hook-instance
  //     invalidator built with just `{label}` (no query) yields `[GMS, label]`,
  //     which still element-wise-prefix-matches every `[GMS, label, query, …]`
  //     consumer key — invalidation fan-out is preserved but now scoped to the
  //     label instead of the whole tenant.
  //   - `exact`/`tree` go LAST so a shorter invalidator key that omits them
  //     still prefix-matches a consumer key that sets them.
  // A bare `genericSearch('')` invalidator (no label) is unchanged: it still
  // yields `[GMS]` and still matches all generic searches.
  genericSearch: (
    search: string,
    desc?: boolean,
    global?: boolean,
    email?: string,
    allTenants?: boolean,
    label?: string,
    exact?: boolean,
    tree?: boolean
  ) => [
    UniqueQueryKeys.GENERIC_MY_SEARCH,
    ...(label ? [label] : []),
    ...(search ? [search] : []),
    ...(desc !== undefined ? [String(desc)] : []),
    ...(global ? [global] : []),
    ...(email ? [email] : []),
    ...(allTenants ? [allTenants] : []),
    ...(exact ? ['exact'] : []),
    ...(tree ? ['tree'] : []),
  ],
  getGavatarProfilePicture: (email: string) => [
    UniqueQueryKeys.GAVATAR_PROFILE_PICTURE,
    email,
  ],
  getKev: () => [UniqueQueryKeys.KEV],
  userAttributes: () => [UniqueQueryKeys.USER_ATTRIBUTES],
  getSeedFilters: ({
    type,
    column,
  }: {
    type: 'domain' | 'ip';
    column: string;
  }) => [UniqueQueryKeys.SEED_FILTERS, type, column],
  getGraphQuery: (query: GraphQuery) => [UniqueQueryKeys.GRAPH_QUERY, query],
  getCapabilities: (filters?: {
    name?: string;
    target?: string;
    executor?: string;
  }) => [
    UniqueQueryKeys.CAPABILITIES,
    ...(filters ? [JSON.stringify(filters)] : []),
  ],
  getApiKeys: () => [UniqueQueryKeys.API_KEYS],
  getPlextracReports: (customerEmail?: string) => [
    UniqueQueryKeys.PLEXTAC_REPORTS,
    ...(customerEmail ? [customerEmail] : []),
  ],
  getReporting: (clientId?: string, customerEmail?: string) => [
    UniqueQueryKeys.REPORTING,
    ...(clientId ? [clientId] : []),
    ...(customerEmail ? [customerEmail] : []),
  ],
  getEvidenceFiles: (riskKey: string) => [
    UniqueQueryKeys.EVIDENCE_FILES,
    riskKey,
  ],
  getCollaboratorQuery: (key: string, email: string) => [
    UniqueQueryKeys.COLLABORATOR_QUERY,
    key,
    email,
  ],
  getPlexTracClientId: (customerEmail?: string) => [
    UniqueQueryKeys.PLEXTAC_CLIENT_ID,
    ...(customerEmail ? [customerEmail] : []),
  ],
  getAegisManagementTasks: (agentId?: string) => [
    UniqueQueryKeys.AEGIS_MANAGEMENT_TASKS,
    ...(agentId ? [agentId] : []),
  ],
  getAIBudget: () => [UniqueQueryKeys.AI_BUDGET],
  csfAssessment: () => ['csf-assessment'] as const,
  assetMetrics: (assetKey: string) => [UniqueQueryKeys.ASSET_METRICS, assetKey],
  aggregatedAssetMetrics: (assetKeys: readonly string[]) => [
    UniqueQueryKeys.ASSET_METRICS,
    'aggregate',
    ...assetKeys,
  ],
  constantineManifest: () => [UniqueQueryKeys.CONSTANTINE_MANIFEST],
  getConversationCost: (conversationId: string) => [
    UniqueQueryKeys.CONVERSATION_COST,
    conversationId,
  ],
  getHuntCost: (huntUuid: string) => [UniqueQueryKeys.HUNT_COST, huntUuid],
  getShares: (email?: string) => [
    UniqueQueryKeys.SHARES,
    ...(email ? [email] : []),
  ],
  getShareResolve: (token?: string) => [
    UniqueQueryKeys.SHARE_RESOLVE,
    ...(token ? [token] : []),
  ],
  // Tenant-deletion resource (global, Praetorian-Admin cross-tenant list).
  // The detail key nests under the list key so invalidating the list prefix
  // also refreshes any open detail query.
  getTenantDeletions: () => [UniqueQueryKeys.TENANT_DELETIONS] as const,
  getTenantDeletion: (id: string) =>
    [UniqueQueryKeys.TENANT_DELETIONS, id] as const,
};

/**
 * The user-scope segment `useGetUserKey` appends as the LAST query-key
 * segment, or `''` when it appends nothing. Exposed separately so callers
 * (useMy) can hand the exact suffix to the friend-aware invalidation matcher
 * in `@/utils/api` without replicating the precedence rules here (ENG-4400).
 */
export function useUserKeySuffix(
  doNotImpersonate?: boolean,
  email?: string
): string {
  const { friend } = useAuth();

  if (!doNotImpersonate && friend) {
    return friend;
  }

  return email ?? '';
}

export function useGetUserKey(
  key: ReadonlyArray<unknown> = [],
  doNotImpersonate?: boolean,
  email?: string
) {
  const suffix = useUserKeySuffix(doNotImpersonate, email);

  if (suffix) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...key, suffix] as any;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return [...key] as any;
}
